import {
  ADD_TICKET_NOTE_TEMP,
  ADMIN_UPDATE_USER,
  ADMIN_UPDATE_USER_SUCCESS,
  ADMIN_UPDATE_USER_ERROR,
  ADMIN_MANUAL_EMAIL_APPROVAL,
  ADMIN_MANUAL_EMAIL_APPROVAL_ERROR,
  ADMIN_MANUAL_EMAIL_APPROVAL_SUCCESS,
  GET_APPREG_STATUS,
  GET_APPREG_STATUS_ERROR,
  GET_APPREG_STATUS_SUCCESS,
  ADMIN_CREATE_USER,
  ADMIN_CREATE_USER_ERROR,
  ADMIN_CREATE_USER_SUCCESS,
  ADMIN_RESET_USER_STATUS,
} from "../actions/accountAdminActions";

import _ from "lodash";
import {DateTime} from "luxon";

interface AccountContact {
  id: number | string;
  [key: string]: unknown;
}

interface AccountUserProfile {
  id: number | string;
  contact?: AccountContact;
  contactId?: number | string;
  [key: string]: unknown;
}

interface AccountUsers {
  mappedUserProfiles: AccountUserProfile[];
  unmappedUserProfiles: AccountUserProfile[];
  approvedUsers: AccountUserProfile[];
  contacts: AccountContact[];
  [key: string]: unknown;
}

interface AppRegistration {
  organizationResponseJson?: string;
  [key: string]: unknown;
}

interface AccountAdminState {
  localTicketNotes: unknown[];
  fetchingAccountReports: boolean;
  fetchAccountReportsComplete: boolean;
  fetchAccountReportsError: unknown;
  accountUsers: AccountUsers;
  updatingAccountUser: boolean;
  updatingAccountUserComplete: boolean;
  updatingAccountUserError: unknown;
  activatedUsers: unknown[];
  updatingManualEmail: boolean;
  updatingManualEmailComplete: boolean;
  updatingManualEmailError: unknown;
  appRegStatus: AppRegistration[];
  fetchingAppReg: boolean;
  fetchAppRegComplete: boolean;
  fetchAppRegError: unknown;
  creatingAccountUser: boolean;
  creatingAccountUserComplete: boolean;
  creatingAccountUserError: unknown;
  lastCreatedUser?: unknown;
  accounts?: unknown;
}

interface AccountAdminAction {
  type: string;
  payload: {
    data?: unknown;
    accountId?: string | number;
  };
}

function hasJsonStructure(str: unknown): boolean {
  if (typeof str !== "string") return false;
  try {
    const result = JSON.parse(str);
    const type = Object.prototype.toString.call(result);
    return type === "[object Object]" || type === "[object Array]";
  } catch (err) {
    return false;
  }
}

const updateMappedUserProfile = (
  mappedUserProfiles: AccountUserProfile[],
  updatedUserProfiles: AccountUserProfile[],
  updatedContacts: AccountContact[]
) => {
  return _.map(mappedUserProfiles, function (el) {
    if (el.id === updatedUserProfiles[0].id) {
      updatedUserProfiles[0].contact = updatedContacts[0];
      return updatedUserProfiles[0];
    }
    return el;
  });
};

const updateContacts = (contacts: AccountContact[], updatedContacts: AccountContact[]) => {
  return _.map(contacts, function (el) {
    if (el.id === updatedContacts[0].id) {
      return updatedContacts[0];
    }
    return el;
  });
};

const appRegistrationsWithOrgJson = (appRegistrations: AppRegistration[]) => {
  return _.map(appRegistrations, (ap) => {
    return {
      ...ap,
      organizationData:
        ap.organizationResponseJson &&
        hasJsonStructure(ap.organizationResponseJson)
          ? JSON.parse(ap.organizationResponseJson as string).value[0]
          : false,
    };
  });
};

const updateWithCreatedUsers = (existingState: AccountUsers, payloadData: { updatedUserProfiles: AccountUserProfile[]; updatedContacts: AccountContact[] }) => {
  const { mappedUserProfiles, unmappedUserProfiles, contacts, approvedUsers } =
    existingState;
  const { updatedContacts, updatedUserProfiles } = payloadData;

  // step 1: remove createdUserProfile from approvedUsers and unmappedUserProfiles
  const filteredApprovedUserProfiles = _.filter(approvedUsers, (au) => {
    const index = updatedUserProfiles.findIndex((e) => e.id === au.id);
    return index === -1;
  });

  const filteredUnmappedUserProfiles = _.filter(unmappedUserProfiles, (uup) => {
    const index = updatedUserProfiles.findIndex((e) => e.id === uup.id);
    return index === -1;
  });

  // step 2: add or replace contacts with new contacts
  // step 2a: remove duplicates
  const filteredContacts = _.filter(contacts, (c) => {
    const index = updatedContacts.findIndex((e) => e.id === c.id);
    return index === -1;
  });

  // step 3a: re-add new contacts
  const mergedContacts = [...filteredContacts, ...updatedContacts];

  // step 4: add new user profiles to mappedUserProfiles
  const combinedUserProfiles = [...mappedUserProfiles, ...updatedUserProfiles];

  // step 3: add contact objects to userProfiles
  const combinedUserProfilesWithContacts = _.map(
    combinedUserProfiles,
    (cup) => {
      const contactIndex = cup.contact
        ? mergedContacts.findIndex((e) => e.id === cup.contact!.id)
        : mergedContacts.findIndex((e) => e.id === cup.contactId);
      return {
        ...cup,
        contact: mergedContacts[contactIndex],
      };
    }
  );

  const result = {
    contacts: mergedContacts,
    mappedUserProfiles: combinedUserProfilesWithContacts,
    approvedUsers: filteredApprovedUserProfiles,
    unmappedUserProfiles: filteredUnmappedUserProfiles,
  };

  return result;
};

export function accountAdminReducer(
  state: AccountAdminState = {
    localTicketNotes: [],
    fetchingAccountReports: false,
    fetchAccountReportsComplete: false,
    fetchAccountReportsError: undefined,
    accountUsers: {
      mappedUserProfiles: [],
      unmappedUserProfiles: [],
      approvedUsers: [],
      contacts: [],
    },
    updatingAccountUser: false,
    updatingAccountUserComplete: false,
    updatingAccountUserError: undefined,
    activatedUsers: [],
    updatingManualEmail: false,
    updatingManualEmailComplete: false,
    updatingManualEmailError: undefined,
    appRegStatus: [],
    fetchingAppReg: false,
    fetchAppRegComplete: false,
    fetchAppRegError: undefined,
    creatingAccountUser: false,
    creatingAccountUserComplete: false,
    creatingAccountUserError: undefined,
  },
  action: AccountAdminAction
) {
  switch (action.type) {
    case GET_APPREG_STATUS:
      return Object.assign({}, state, {
        fetchingAppReg: true,
        fetchAppRegComplete: false,
        fetchAppRegError: undefined,
      });
    case GET_APPREG_STATUS_SUCCESS:
      return Object.assign({}, state, {
        appRegStatus: appRegistrationsWithOrgJson(action.payload.data as AppRegistration[]),
        fetchingAppReg: false,
        fetchAppRegComplete: true,
        fetchAppRegError: undefined,
      });
    case GET_APPREG_STATUS_ERROR:
      return Object.assign({}, state, {
        fetchingAppReg: false,
        fetchAppRegComplete: true,
        fetchAppRegError: action.payload.data,
      });
    case ADMIN_MANUAL_EMAIL_APPROVAL:
      return Object.assign({}, state, {
        updatingManualEmail: true,
        updatingManualEmailComplete: false,
      });
    case ADMIN_MANUAL_EMAIL_APPROVAL_ERROR:
      return Object.assign({}, state, {
        updatingManualEmail: false,
        updatingManualEmailComplete: true,
        updatingManualEmailError: action.payload.data,
      });
    case ADMIN_MANUAL_EMAIL_APPROVAL_SUCCESS:
      return Object.assign({}, state, {
        updatingManualEmail: false,
        updatingManualEmailComplete: true,
        accounts: action.payload.data,
      });
    case ADD_TICKET_NOTE_TEMP: {
      return Object.assign({}, state, {
        localTicketNotes: [
          ...state.localTicketNotes,
          Object.assign({}, action.payload.data, {
            timestamp: DateTime.local().toMillis(),
          }),
        ],
      });
    }
    case ADMIN_UPDATE_USER:
      return Object.assign({}, state, {
        updatingAccountUser: true,
        updatingAccountUserComplete: false,
      });
    case ADMIN_UPDATE_USER_ERROR:
      return Object.assign({}, state, {
        updatingAccountUser: false,
        updatingAccountUserComplete: true,
        updatingAccountUserError: action.payload.data,
      });
    case ADMIN_UPDATE_USER_SUCCESS:
      return Object.assign({}, state, {
        updatingAccountUser: false,
        updatingAccountUserComplete: true,
        accountUsers: Object.assign({}, state.accountUsers, {
          ...state.accountUsers,
          mappedUserProfiles: updateMappedUserProfile(
            state.accountUsers.mappedUserProfiles,
            (action.payload.data as { updatedUserProfiles: AccountUserProfile[]; updatedContacts: AccountContact[] }).updatedUserProfiles,
            (action.payload.data as { updatedUserProfiles: AccountUserProfile[]; updatedContacts: AccountContact[] }).updatedContacts
          ),
          contacts: updateContacts(
            state.accountUsers.contacts,
            (action.payload.data as { updatedUserProfiles: AccountUserProfile[]; updatedContacts: AccountContact[] }).updatedContacts
          ),
        }),
      });
    case ADMIN_CREATE_USER:
      return Object.assign({}, state, {
        creatingAccountUser: true,
        creatingAccountUserComplete: false,
      });
    case ADMIN_CREATE_USER_ERROR:
      return Object.assign({}, state, {
        creatingAccountUser: false,
        creatingAccountUserComplete: true,
        creatingAccountUserError: action.payload.data,
      });
    case ADMIN_CREATE_USER_SUCCESS:
      return Object.assign({}, state, {
        creatingAccountUser: false,
        creatingAccountUserComplete: true,
        accountUsers: updateWithCreatedUsers(
          state.accountUsers,
          action.payload.data as { updatedUserProfiles: AccountUserProfile[]; updatedContacts: AccountContact[] }
        ),
        lastCreatedUser: action.payload.data,
      });
    case ADMIN_RESET_USER_STATUS:
      return Object.assign({}, state, {
        creatingAccountUser: false,
        creatingAccountUserComplete: false,
        creatingAccountUserError: undefined,
        lastCreatedUser: undefined,
      });
    default:
      return state;
  }
}
